import { toggleView as toggleDuplicateView } from './actions/duplicates';

import {
  changeAddressFilterGroup,
  changePropertyFilterGroup,
  changeSaleFilterGroup,
  changeIndustrialFilterGroup,
  changeMultifamilyFilterGroup,
  changeSearchText,
  setTags,
  setAssetClasses,
  setAssociationFilters,
  setAvailabilities,
  setBuildingClasses,
  setInvestmentTypes,
  setGroupFilterIds,
  changePropertyPage,
  changeSort,
  selectExcludeTags
} from './actions/query';

import { setUserBounds } from './actions/coordinates';

import { switchTab } from './actions/ui';
import {
  stringToArray,
  arrayToString,
  stringToObject,
  objectToString,
  stringToBoolean,
  booleanToString
} from 'helpers/formatters/string_formats';

const queryParams = {
  tab: {
    action: value => value ? switchTab(value) : { type: null },
    selector: state => state.ui.currentTab
  },
  building_number: {
    action: value => changeAddressFilterGroup({ buildingNumber: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.buildingNumber || ''
  },
  street: {
    action: value => changeAddressFilterGroup({ street: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.street || ''
  },
  cities: {
    action: value => changeAddressFilterGroup({ cities: value }),
    defaultValue: [],
    selector: state => state.query.addressFilter.cities || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  market: {
    action: value => changeAddressFilterGroup({ market: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.market || ''
  },
  submarket: {
    action: value => changeAddressFilterGroup({ subMarket: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.subMarket || ''
  },
  microMarket: {
    action: value => changeAddressFilterGroup({ microMarket: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.microMarket || ''
  },
  state: {
    action: value => changeAddressFilterGroup({ state: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.state || ''
  },
  zip: {
    action: value => changeAddressFilterGroup({ zip: value }),
    defaultValue: '',
    selector: state => state.query.addressFilter.zip || ''
  },
  exclude_tags: {
    action: selectExcludeTags,
    defaultValue: false,
    selector: state => state.query.excludeTags
  },
  max_sale_price: {
    action: value => changeSaleFilterGroup({ maxSalesPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxSalesPrice || ''
  },
  min_sale_price: {
    action: value => changeSaleFilterGroup({ minSalesPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minSalesPrice || ''
  },
  max_sales_date: {
    action: value => changeSaleFilterGroup({ maxSalesDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxSalesDate || ''
  },
  min_sales_date: {
    action: value => changeSaleFilterGroup({ minSalesDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minSalesDate || ''
  },
  max_lease_price: {
    action: value => changeSaleFilterGroup({ maxLeasePrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxLeasePrice || ''
  },
  min_lease_price: {
    action: value => changeSaleFilterGroup({ minLeasePrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minLeasePrice || ''
  },
  max_lease_date: {
    action: value => changeSaleFilterGroup({ maxLeaseDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxLeaseDate || ''
  },
  min_lease_date: {
    action: value => changeSaleFilterGroup({ minLeaseDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minLeaseDate || ''
  },
  max_lease_execution_date: {
    action: value => changeSaleFilterGroup({ maxLeaseExecutionDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxLeaseExecutionDate || ''
  },
  min_lease_execution_date: {
    action: value => changeSaleFilterGroup({ minLeaseExecutionDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minLeaseExecutionDate || ''
  },
  max_lease_expiration_date: {
    action: value => changeSaleFilterGroup({ maxLeaseExpirationDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxLeaseExpirationDate || ''
  },
  min_lease_expiration_date: {
    action: value => changeSaleFilterGroup({ minLeaseExpirationDate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minLeaseExpirationDate || ''
  },
  max_lease_size: {
    action: value => changeSaleFilterGroup({ maxLeaseSize: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxLeaseSize || ''
  },
  min_lease_size: {
    action: value => changeSaleFilterGroup({ minLeaseSize: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minLeaseSize || ''
  },
  max_sf_price: {
    action: value => changeSaleFilterGroup({ maxSfPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxSfPrice || ''
  },
  min_sf_price: {
    action: value => changeSaleFilterGroup({ minSfPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minSfPrice || ''
  },
  max_unit_price: {
    action: value => changeSaleFilterGroup({ maxUnitPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxUnitPrice || ''
  },
  min_unit_price: {
    action: value => changeSaleFilterGroup({ minUnitPrice: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minUnitPrice || ''
  },
  max_cap_rate: {
    action: value => changeSaleFilterGroup({ maxCapRate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.maxCapRate || ''
  },
  min_cap_rate: {
    action: value => changeSaleFilterGroup({ minCapRate: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.minCapRate || ''
  },
  lease_space_type: {
    action: value => changeSaleFilterGroup({ leaseSpaceType: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.leaseSpaceType || ''
  },
  lease_type: {
    action: value => changeSaleFilterGroup({ leaseType: value }),
    defaultValue: '',
    selector: state => state.query.saleFilter.leaseType || ''
  },
  apn_number: {
    action: value => changePropertyFilterGroup({ apnNumber: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.apnNumber || ''
  },
  building_class_a: {
    action: value => changeAddressFilterGroup({ buildingClassA: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.buildingClassA || ''
  },
  building_class_b: {
    action: value => changeAddressFilterGroup({ buildingClassB: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.buildingClassB || ''
  },
  building_class_c: {
    action: value => changeAddressFilterGroup({ buildingClassC: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.buildingClassC || ''
  },
  lotUnits: {
    action: value => changePropertyFilterGroup({ lotUnits: value }),
    defaultValue: 'sf',
    selector: state => state.query.propertyFilter.lotUnits || ''
  },
  min_lot_size: {
    action: value => changePropertyFilterGroup({ minLotSize: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minLotSize || ''
  },
  max_lot_size: {
    action: value => changePropertyFilterGroup({ maxLotSize: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxLotSize || ''
  },
  min_available_sf: {
    action: value => changePropertyFilterGroup({ minAvailableSF: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minAvailableSF || ''
  },
  max_available_sf: {
    action: value => changePropertyFilterGroup({ maxAvailableSF: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxAvailableSF || ''
  },
  max_building_size: {
    action: value => changePropertyFilterGroup({ maxBuildingSize: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxBuildingSize || ''
  },
  min_building_size: {
    action: value => changePropertyFilterGroup({ minBuildingSize: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minBuildingSize || ''
  },
  max_year_built: {
    action: value => changePropertyFilterGroup({ maxYearBuilt: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxYearBuilt || ''
  },
  min_year_built: {
    action: value => changePropertyFilterGroup({ minYearBuilt: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minYearBuilt || ''
  },
  min_num_units: {
    action: value => changePropertyFilterGroup({ minNumUnits: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minNumUnits || ''
  },
  max_num_units: {
    action: value => changePropertyFilterGroup({ maxNumUnits: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxNumUnits || ''
  },
  min_ceiling_height: {
    action: value => changePropertyFilterGroup({ minCeilingHeight: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minCeilingHeight || ''
  },
  max_ceiling_height: {
    action: value => changePropertyFilterGroup({ maxCeilingHeight: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxCeilingHeight || ''
  },
  max_mortgage_amount: {
    action: value => changePropertyFilterGroup({ maxMortgageAmount: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxMortgageAmount || ''
  },
  min_mortgage_amount: {
    action: value => changePropertyFilterGroup({ minMortgageAmount: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minMortgageAmount || ''
  },
  max_mortgage_date: {
    action: value => changePropertyFilterGroup({ maxMortgageRecordedDate: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.maxMortgageRecordedDate || ''
  },
  min_mortgage_date: {
    action: value => changePropertyFilterGroup({ minMortgageRecordedDate: value }),
    defaultValue: '',
    selector: state => state.query.propertyFilter.minMortgageRecordedDate || ''
  },
  available_rail_provider: {
    action: value => changeIndustrialFilterGroup({ availableRailProvider: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.availableRailProvider || ''
  },
  drive_through_access: {
    action: value => changeIndustrialFilterGroup({ driveThroughAccess: value }),
    defaultValue: false,
    selector: state => state.query.industrialFilter.driveThroughAccess || false
  },
  ready: {
    action: value => changeIndustrialFilterGroup({ ready: value }),
    defaultValue: false,
    selector: state => state.query.industrialFilter.ready || false
  },
  served: {
    action: value => changeIndustrialFilterGroup({ served: value }),
    defaultValue: false,
    selector: state => state.query.industrialFilter.served || false
  },
  max_jib_crane_span: {
    action: value => changeIndustrialFilterGroup({ maxJibCraneSpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxJibCraneSpan || '',
    stringToValue: value => Number(value)
  },
  min_jib_crane_span: {
    action: value => changeIndustrialFilterGroup({ minJibCraneSpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minJibCraneSpan || '',
    stringToValue: value => Number(value)
  },
  lot_border_type: {
    action: value => changeIndustrialFilterGroup({ lotBorderType: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.lotBorderType || ''
  },
  lot_stabilization: {
    action: value => changeIndustrialFilterGroup({ lotStabilization: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.lotStabilization || ''
  },
  max_acres_stabilized: {
    action: value => changeIndustrialFilterGroup({ maxAcresStabilized: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxAcresStabilized || '',
    stringToValue: value => Number(value)
  },
  max_bridge_crane_span: {
    action: value => changeIndustrialFilterGroup({ maxBridgeCraneSpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxBridgeCraneSpan || '',
    stringToValue: value => Number(value)
  },
  max_num_bridge_cranes: {
    action: value => changeIndustrialFilterGroup({ maxNumBridgeCranes: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxNumBridgeCranes || '',
    stringToValue: value => Number(value)
  },
  max_num_jib_cranes: {
    action: value => changeIndustrialFilterGroup({ maxNumJibCranes: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxNumJibCranes || '',
    stringToValue: value => Number(value)
  },
  max_ready_capacity: {
    action: value => changeIndustrialFilterGroup({ maxReadyCapacity: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxReadyCapacity || '',
    stringToValue: value => Number(value)
  },
  max_ready_span: {
    action: value => changeIndustrialFilterGroup({ maxReadySpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.maxReadySpan || '',
    stringToValue: value => Number(value)
  },
  min_acres_stabilized: {
    action: value => changeIndustrialFilterGroup({ minAcresStabilized: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minAcresStabilized || '',
    stringToValue: value => Number(value)
  },
  min_bridge_crane_span: {
    action: value => changeIndustrialFilterGroup({ minBridgeCraneSpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minBridgeCraneSpan || '',
    stringToValue: value => Number(value)
  },
  min_num_bridge_cranes: {
    action: value => changeIndustrialFilterGroup({ minNumBridgeCranes: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minNumBridgeCranes || '',
    stringToValue: value => Number(value)
  },
  min_num_jib_cranes: {
    action: value => changeIndustrialFilterGroup({ minNumJibCranes: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minNumJibCranes || '',
    stringToValue: value => Number(value)
  },
  min_ready_capacity: {
    action: value => changeIndustrialFilterGroup({ minReadyCapacity: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minReadyCapacity || '',
    stringToValue: value => Number(value)
  },
  min_ready_span: {
    action: value => changeIndustrialFilterGroup({ minReadySpan: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.minReadySpan || '',
    stringToValue: value => Number(value)
  },
  served_rail_provider: {
    action: value => changeIndustrialFilterGroup({ servedRailProvider: value }),
    defaultValue: '',
    selector: state => state.query.industrialFilter.servedRailProvider || ''
  },
  duplicates: {
    action: value => toggleDuplicateView(!!value),
    defaultValue: false,
    selector: state => state.duplicates.viewActive,
    stringToValue: stringToBoolean,
    valueToString: booleanToString
  },
  property_group: {
    action: setGroupFilterIds,
    defaultValue: [],
    selector: state => state.query.researchGroupIds,
    stringToValue(string) {
      return stringToArray(string).map(value => !value || value === 'ungrouped' ? value : parseInt(value));
    },
    valueToString: arrayToString
  },
  associations: {
    action: setAssociationFilters,
    defaultValue: {},
    selector: state => state.query.associationFilters,
    stringToValue: stringToObject,
    valueToString: objectToString,
  },
  availabilities: {
    action: setAvailabilities,
    defaultValue: [],
    selector: state => state.query.availabilities || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  asset_classes: {
    action: setAssetClasses,
    defaultValue: [],
    selector: state => state.query.assetClasses || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  building_classes: {
    action: setBuildingClasses,
    defaultValue: [],
    selector: state => state.query.buildingClasses || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  investment_types: {
    action: setInvestmentTypes,
    defaultValue: [],
    selector: state => state.query.investmentTypes || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  search_text: {
    action: changeSearchText,
    defaultValue: '',
    selector: state => state.query.searchText || ''
  },
  property_page: {
    action: value => value ? changePropertyPage(value) : { type: null },
    selector: state => state.query.propertyPage,
    stringToValue: value => Number(value)
  },
  tags: {
    action: setTags,
    defaultValue: [],
    selector: state => state.query.tags || [],
    stringToValue: stringToArray,
    valueToString: arrayToString
  },
  property_sort: {
    action: value => changeSort(value || null),
    defaultValue: null,
    selector: state => state.query.propertySort || null
  },
  user_bounds: {
    action: setUserBounds,
    defaultValue: null || {},
    selector: state => state.coordinates.userBounds || {} || null,
    stringToValue: stringToObject,
    valueToString: objectToString
  },
  max_beds: {
    action: value => changeMultifamilyFilterGroup({ maxBeds: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.maxBeds || ''
  },
  max_baths: {
    action: value => changeMultifamilyFilterGroup({ maxBaths: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.maxBaths || ''
  },
  max_size: {
    action: value => changeMultifamilyFilterGroup({ maxSize: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.maxSize || ''
  },
  max_rent: {
    action: value => changeMultifamilyFilterGroup({ maxRent: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.maxRent || ''
  },
  max_market_rent: {
    action: value => changeMultifamilyFilterGroup({ maxMarketRent: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.maxMarketRent || ''
  },
  min_beds: {
    action: value => changeMultifamilyFilterGroup({ minBeds: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.minBeds || ''
  },
  min_baths: {
    action: value => changeMultifamilyFilterGroup({ minBaths: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.minBaths || ''
  },
  min_size: {
    action: value => changeMultifamilyFilterGroup({ minSize: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.minSize || ''
  },
  min_rent: {
    action: value => changeMultifamilyFilterGroup({ minRent: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.minRent || ''
  },
  min_market_rent: {
    action: value => changeMultifamilyFilterGroup({ minMarketRent: value }),
    defaultValue: '',
    selector: state => state.query.multifamilyFilter.minMarketRent || ''
  }
};

export default queryParams;
